import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "./Contactspage.module.css";
import ContactManager from "../ContactManager/ContactManager";
import HeaderBar from "../HeaderBar/HeaderBar";
import { useTeamStore } from "../../Store/teamStore";
import { setContactOwnerId } from "../../Store/apiStore";
import { getActiveOwnerId, primeContext } from "../../lib/activeContext";
import {
  listContacts,
  createContact,
  updateContact as updateContactApi,
  deleteContact as deleteContactApi,
  bulkDeleteContacts as bulkDeleteContactsApi,
  blockContact as blockContactApi,
  unblockContact as unblockContactApi,
  uploadContacts,
  syncNativeContacts,
  listBlacklist,
  addToBlacklist,
  removeFromBlacklist,
  getAgentSpamSettings,
  updateAgentSpamSettings,
  updateForwardPhone,
  updateContactRouting,
  fetchUserDetails,
  listAgents,
  getGoogleSyncStatus,         // updated (was getGoogleAuthUrl)
  triggerGoogleContactsSync,   // new
  isSyncContactSkip,
} from "../../Store/apiStore";
import decodeToken from "../../lib/decodeToken";
import { markSetupStep } from "../../lib/setupScore";
import { trackEvent } from "../../lib/analytics";

/*
 * ContactsPage props:
 *
 *   autoOpenSync (default false)
 *     If true, ContactManager pops the Sync bottom-sheet on first
 *     paint. Used by the onboarding ContactSyncStep wrapper; default
 *     off so the dashboard mount is unchanged.
 *
 *   onSyncDialogClose (default null)
 *     Fires when the user dismisses (or completes) the Sync sheet.
 *     The onboarding wrapper uses this to kick off its intro.js
 *     feature tour. Dashboard never passes it.
 *
 *   hideHeader (default false)
 *     Hides the purple "Contacts" header bar. The onboarding wrapper
 *     turns this on so the wrapper's own chrome (skip / continue)
 *     isn't competing with the dashboard's back button.
 */
const ContactsPage = ({
  autoOpenSync = false,
  onSyncDialogClose,
  hideHeader = false,
  oauthReturnTo,
} = {}) => {
  const navigate  = useNavigate();
  const location  = useLocation();

  const token            = localStorage.getItem("token") || "";
  const decodeTokenData  = decodeToken(token);
  const userIdFromToken  = decodeTokenData?.id || "";
  const [userId, setUserId] = useState(userIdFromToken);

  // agentId from query param or sessionStorage
  const params       = new URLSearchParams(location.search);
  const agentIdParam = params.get("agentId") || sessionStorage.getItem("SelectAgentId") || "";

  // Team member ke contacts sirf usi business ke jisme login kiya hai (token ka ownerId).
  const isTeamMemberCP  = !!localStorage.getItem("teamOwnerId");
  const selectedOwnerId = isTeamMemberCP ? getActiveOwnerId() || "" : "";
  const teams           = useTeamStore((s) => s.teams);
  const loadTeams       = useTeamStore((s) => s.loadTeams);

  useEffect(() => {
    if (isTeamMemberCP) loadTeams();
  }, [isTeamMemberCP, loadTeams]);

  // Page chhodte waqt header null nahi, login wale business pe wapas — warna baaki contact calls 400 dete hain.
  useEffect(() => () => primeContext(setContactOwnerId), []);

  const activeTeam = (teams || []).find((t) => t.ownerId === selectedOwnerId);
  const selectedOwnerName =
    activeTeam?.ownerBusiness || activeTeam?.ownerCompany || activeTeam?.ownerName || activeTeam?.ownerEmail || "";

  // Support ko sirf dekhna — admin poora manage karta hai.
  const contactsReadOnly = isTeamMemberCP && activeTeam?.role !== "admin";

  const [contacts,       setContacts]       = useState([]);
  const [blacklist,      setBlacklist]      = useState([]);
  const [agents,         setAgents]         = useState([]);
  const [routingEnabled, setRoutingEnabled] = useState(false);
  const [forwardPhone,   setForwardPhone]   = useState("");
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  // Sync state shown on contacts page while a background sync runs
  const [syncStatus,     setSyncStatus]     = useState(null); // null | "syncing" | "done" | "error"

  // ── Detect ?google_sync=success after OAuth redirect ────────────────────────
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get("google_sync") !== "success") return;

    // ContactSyncStep onboarding bounce: if we kicked the OAuth from
    // /contact-sync, the backend's default callback lands us on
    // /contacts. Detect the flag, redirect to /contact-sync (which
    // re-mounts THIS same component inside ContactSyncStep, sees the
    // success param again, and runs the actual sync there). One-shot
    // — flag is cleared so a refresh of /contacts doesn't bounce.
    const expectingContactSyncReturn =
      localStorage.getItem("contactSync.expectGoogleSyncReturn") === "true";
    const onContactSyncRoute = location.pathname.startsWith("/contact-sync");
    if (expectingContactSyncReturn && !onContactSyncRoute) {
      localStorage.removeItem("contactSync.expectGoogleSyncReturn");
      navigate("/contact-sync?google_sync=success", { replace: true });
      return;
    }

    // Clean the URL first so refresh doesn't re-trigger
    navigate(location.pathname + (agentIdParam ? `?agentId=${agentIdParam}` : ""), {
      replace: true,
    });
    // Google Contacts authorised. The backend only redirects with
    // ?google_sync=success after storing the Google token (Contactcontroller
    // googleCallback). Fired here, AFTER the onboarding bounce above: during
    // onboarding this handler runs twice (/contacts, then /contact-sync) and
    // only the pass that actually runs the sync reaches this line.
    trackEvent("google_contacts_connected", {
      source: onContactSyncRoute ? "contact_sync_onboarding" : "contacts_page",
    });
    // Now trigger the sync
    handleGoogleSyncTrigger();
  }, []);

  // ── Native contacts bridge ───────────────────────────────────────────────────
  // React Native posts a message after user grants contacts permission
  useEffect(() => {
    const handleNativeMessage = async (event) => {
      try {
        const data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;

        if (data?.type === "CONTACTS_DATA" && Array.isArray(data.contacts)) {
          console.log("[ContactsPage] Native contacts received:", data.contacts.length);
          await handleNativeContactsReceived(data.contacts);
        }
      } catch (err) {
        console.error("[ContactsPage] Native bridge parse error:", err);
      }
    };

    // iOS uses window, Android uses document
    window.addEventListener("message", handleNativeMessage);
    document.addEventListener("message", handleNativeMessage);

    return () => {
      window.removeEventListener("message", handleNativeMessage);
      document.removeEventListener("message", handleNativeMessage);
    };
  }, []);

  // ── Initial load ─────────────────────────────────────────────────────────────
  // For an admin team member, set the X-Owner-Id header (selected company) BEFORE
  // fetching so the backend scopes correctly, and wait until a company is chosen.
  // Owners just use their own account (no header).
  useEffect(() => {
    if (isTeamMemberCP) {
      if (!selectedOwnerId) return; // wait for company resolution
      setContactOwnerId(selectedOwnerId);
    } else {
      setContactOwnerId(null);
    }
    loadAll();
  }, [userId, agentIdParam, selectedOwnerId, isTeamMemberCP]);

  /*
   * `silent` — refresh the data WITHOUT tearing the screen down.
   *
   * The body renders `loading ? <spinner> : error ? <errorState> : <ContactManager>`,
   * so flipping `loading` unmounts ContactManager entirely: the spinner flashes,
   * any open dialog closes, and the user's tab / filter / scroll position reset.
   * That is acceptable for a first load; it is not acceptable for a background
   * refresh after a sync, where the user is looking at the result.
   *
   * `setError` is skipped too. A silent refresh that fails must leave the list
   * the user is already reading intact — replacing it with "Failed to load
   * contacts" over a network hiccup would throw away perfectly valid data.
   *
   * Defaulted parameter, so every existing bare `loadAll()` call is unchanged.
   */
  const loadAll = async ({ silent = false } = {}) => {
    if (!userId) {
      if (!silent) setLoading(false);
      return;
    }
    if (!silent) setLoading(true);
    try {
      const [contactsRes, blacklistRes, agentsRes, userRes] = await Promise.all([
        // 403 (is business me contacts access nahi) chupana nahi — error dikhana hai.
        listContacts({ page: 1, limit: 500 }).catch((e) => {
          if (e?.response?.status === 403) throw e;
          return { contacts: [], total: 0 };
        }),
        listBlacklist(agentIdParam || undefined).catch(() => []),
        listAgents().catch(() => []),
        fetchUserDetails(userId).catch(() => null),
      ]);

      let allContacts = contactsRes?.contacts || contactsRes?.data || [];
      const total = contactsRes?.total || allContacts.length;

      // Auto-load remaining pages if more than 500 contacts
      if (total > allContacts.length) {
        const totalPages = Math.ceil(total / 500);
        const remainingPages = [];
        for (let p = 2; p <= totalPages; p++) {
          remainingPages.push(listContacts({ page: p, limit: 500 }).catch(() => ({ contacts: [] })));
        }
        const results = await Promise.all(remainingPages);
        results.forEach((r) => {
          const moreContacts = r?.contacts || r?.data || [];
          allContacts = [...allContacts, ...moreContacts];
        });
      }

      setContacts(allContacts);
      setBlacklist(Array.isArray(blacklistRes) ? blacklistRes : blacklistRes?.data || []);

      // Team member ke apne agent hote hi nahi — wo owner ke hote hain, isliye
      // apni id se filter karne par list hamesha khaali aa rahi thi.
      const contactsOwner = selectedOwnerId || userId;
      const myAgents = (Array.isArray(agentsRes) ? agentsRes : [])
        .filter(a => a.userId === contactsOwner);
      setAgents(myAgents);

      const user = userRes?.data || userRes;
      setRoutingEnabled(user?.contactRoutingEnabled || false);
      setForwardPhone(user?.forwardPhone || "");
    } catch (err) {
      console.error("[ContactsPage] loadAll error:", err);
      if (!silent) {
        setError(
          err?.response?.status === 403
            ? err.response.data?.error || "You don't have access to contacts on this account."
            : "Failed to load contacts",
        );
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // ── Google sync trigger (called after OAuth redirect OR from SyncDialog) ─────
  const handleGoogleSyncTrigger = async () => {
    setSyncStatus("syncing");
    try {
      const result = await triggerGoogleContactsSync();
      console.log("[ContactsPage] Google sync complete:", result);
      try { await isSyncContactSkip(); } catch (e) { console.error("[ContactsPage] skip flag update error:", e); }
      /*
       * Setup Score: contactsSynced (10 pts).
       * Idempotent on the backend so re-syncs are safe — we fire on
       * EVERY success path (Google sync / native sync / file import /
       * manual add) because the user's intent matches the step in
       * every case. The setup-score check on FastAgentDetails picks
       * up the new state on next mount / refresh tick.
       */
      try {
        if (agentIdParam) markSetupStep("contactsSynced", agentIdParam);
      } catch (_e) { /* never let score plumbing fail a sync */ }
      // GA4: contacts synced → spam filtering / whitelist set.
      trackEvent("spam_filtering_enabled", {
        contacts_synced_count: (result?.inserted || 0) + (result?.updated || 0),
      });
      // Google Contacts connected. When Google is ALREADY authorised (the token
      // is shared with Calendar, and copied from a Google sign-up) the Sync
      // dialog calls this directly with no OAuth round-trip, so the
      // ?google_sync=success handler above never runs and the connect was never
      // tracked. Setup Ready and dashboard More options both reach it via
      // /contacts. Success path only: this function's catch swallows errors.
      // After an OAuth return the handler above has already fired, and the
      // once-per-user guard drops this repeat.
      trackEvent("google_contacts_connected", {
        source: location.pathname.startsWith("/contact-sync") ? "contact_sync_onboarding" : "contacts_page",
      });
      await loadAll();
    //  window.location.reload(true);
      const parts = [];
      if (result?.inserted > 0) parts.push(`${result.inserted} new`);
      if (result?.updated > 0) parts.push(`${result.updated} duplicate (updated)`);
      setSyncStatus(parts.length > 0 ? `done:Google — ${parts.join(", ")}` : "done");
      setTimeout(() => setSyncStatus(null), 6000);
    } catch (err) {
      console.error("[ContactsPage] Google sync error:", err);
      // If token expired, needsAuth flag comes from backend
      if (err?.response?.data?.needsAuth) {
        setSyncStatus("auth_needed");
      } else {
        setSyncStatus("error");
        setTimeout(() => setSyncStatus(null), 4000);
      }
    }
  };

  // ── Native contacts received from RN bridge ──────────────────────────────────
  const handleNativeContactsReceived = async (nativeContacts) => {
    setSyncStatus("syncing");
    try {
      const res = await syncNativeContacts(nativeContacts);
      console.log("[ContactsPage] Native sync complete:", res);
      // Setup Score: contactsSynced (see handleGoogleSyncTrigger note).
      try {
        if (agentIdParam) markSetupStep("contactsSynced", agentIdParam);
      } catch (_e) {}
      // GA4: contacts synced → spam filtering / whitelist set.
      trackEvent("spam_filtering_enabled", {
        contacts_synced_count: (res?.inserted || 0) + (res?.updated || 0),
      });
      await loadAll();
      const parts = [];
      if (res?.inserted > 0) parts.push(`${res.inserted} new`);
      if (res?.updated > 0) parts.push(`${res.updated} already existed`);
      setSyncStatus(parts.length > 0 ? `done:${parts.join(", ")}` : "done");
      setTimeout(() => setSyncStatus(null), 4000);
    } catch (err) {
      console.error("[ContactsPage] Native sync error:", err);
      setSyncStatus("error");
      setTimeout(() => setSyncStatus(null), 4000);
    }
  };

  // ── Sync handler passed to ContactManager ────────────────────────────────────
  const handleSync = async (type, file) => {
    try {
      /*
       * "refresh" — the native shell finished a contacts sync.
       *
       * ContactManager's RN listener fires `onSync("refresh")` on
       * CONTACTS_API_SUCCESS, but this function only ever handled
       * google / file / phone_picker / native, so the call fell through and
       * returned having done nothing. The upload had genuinely succeeded
       * (verified server-side: inserted 0, updated 309, failed 0) — the list
       * simply was never told. Navigating away and back remounted the page and
       * ran loadAll, which is why the contacts appeared "only after going back".
       *
       * Refreshed SILENTLY on purpose: a plain loadAll() would unmount
       * ContactManager behind the spinner and close the sync dialog the user is
       * still reading.
       *
       * Deliberately NOT fetchAndSyncContacts() — contactStore caches for 3
       * minutes and would no-op immediately after a sync, reproducing the exact
       * bug. loadAll talks to the API directly and sets a fresh array, which
       * ContactManager mirrors into the store on prop change.
       */
      if (type === "refresh") {
        await loadAll({ silent: true });
        return;
      }

      if (type === "google") {
        // SyncDialog already handles the check + trigger flow
        // This is only called if we need to directly start OAuth
        const isRN = !!window.ReactNativeWebView;
        const res = await getGoogleSyncStatus({ isMobile: isRN, flow: "contacts" });
        if (res.hasToken) {
          await handleGoogleSyncTrigger();
        } else if (res.authUrl) {
          if (isRN) {
            // RN WebView: hand the URL to native (opens InAppBrowser, deep
            // link returns to /contacts). Plain location.href won't work here.
            window.ReactNativeWebView.postMessage(
              JSON.stringify({ type: "OPEN_GOOGLE_AUTH", authUrl: res.authUrl, flow: "contacts" })
            );
          } else {
            window.location.href = res.authUrl;
          }
        }
        return;
      }

      if (type === "file" && file) {
        setSyncStatus("syncing");
        const formData = new FormData();
        formData.append("file", file);
        const res = await uploadContacts(formData);
        // Setup Score: contactsSynced (file import path).
        try {
          if (agentIdParam) markSetupStep("contactsSynced", agentIdParam);
        } catch (_e) {}
        await loadAll();
        const parts = [];
        if (res?.inserted > 0) parts.push(`${res.inserted} new`);
        if (res?.updated > 0) parts.push(`${res.updated} duplicate (updated)`);
        if (res?.failed > 0) parts.push(`${res.failed} failed`);
        setSyncStatus(parts.length > 0 ? `done:${parts.join(", ")}` : "done");
        setTimeout(() => setSyncStatus(null), 6000);
        return res;
      }

      if (type === "phone_picker" && Array.isArray(file)) {
        // Browser Contact Picker — contacts already picked, sync them
        setSyncStatus("syncing");
        try {
          const res = await syncNativeContacts(file);
          // Setup Score: contactsSynced (phone picker path).
          try {
            if (agentIdParam) markSetupStep("contactsSynced", agentIdParam);
          } catch (_e) {}
          await loadAll();
          setSyncStatus("done");
          setTimeout(() => setSyncStatus(null), 2000);
          return res; // return result so SyncDialog can show success/failed counts
        } catch (err) {
          console.error("[ContactsPage] phone_picker sync error:", err);
          setSyncStatus("error");
          setTimeout(() => setSyncStatus(null), 4000);
          throw err; // re-throw so SyncDialog can show error
        }
      }

      if (type === "native") {
        // Request contacts from React Native
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({ type: "REQUEST_CONTACTS" }),
          );
          // Response comes via the message listener above
        } else {
          console.warn("[ContactsPage] ReactNativeWebView not available");
        }
        return;
      }
    } catch (err) {
      console.error("[ContactsPage] sync error:", err);
      setSyncStatus("error");
      setTimeout(() => setSyncStatus(null), 4000);
    }
  };

  // ── Contact handlers ──────────────────────────────────────────────────────────
  // const handleCreateContact = async (form) => {
  //   try {
  //     const res = await createContact(form);
  //     const newContact = res?.data || res;
  //     setContacts(prev => [newContact, ...prev]);
  //   } catch (err) {
  //     console.error("[ContactsPage] createContact:", err);
  //   }
  // };

  // const handleUpdateContact = async (id, form) => {
  //   try {
  //     const res = await updateContactApi(id, form);
  //     const updated = res?.data || res;
  //     setContacts(prev => prev.map(c => c.id === id ? { ...c, ...updated } : c));
  //   } catch (err) {
  //     console.error("[ContactsPage] updateContact:", err);
  //   }
  // };

  // ContactsPage.jsx

const handleCreateContact = async (form) => {
  try {
    const res = await createContact(form);
    // Duplicate detected — return raw response for ContactManager to handle
    if (res?.isDuplicate || res?.existsInAgentContacts) return res;
    // Check if backend rejected the phone number
    if (res?.failed > 0 && res?.errors?.length > 0) {
      const reason = res.errors[0]?.reason || "Invalid phone number";
      throw new Error(reason);
    }
    const newContact = res?.contact || res?.data || res;
    setContacts(prev => [newContact, ...prev]);
    // Setup Score: contactsSynced (manual add path). Same idempotent
    // backend mark used by the sync / import handlers above.
    try {
      if (agentIdParam) markSetupStep("contactsSynced", agentIdParam);
    } catch (_e) {}
    loadAll();
    return newContact;
  } catch (err) {
    console.error("[ContactsPage] createContact:", err);
    throw err; // Re-throw so ContactManager can show error
  }
};

const handleUpdateContact = async (id, form) => {
  try {
    const res = await updateContactApi(id, form);
    const updated = res?.data || res;
    setContacts(prev => prev.map(c => c.id === id ? { ...c, ...updated } : c));
    loadAll();
    return updated;
  } catch (err) {
    console.error("[ContactsPage] updateContact:", err);
  }
};

  const handleDeleteContact = async (id) => {
    try {
      await deleteContactApi(id);
      setContacts(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      console.error("[ContactsPage] deleteContact:", err);
    }
  };

  const handleBlockContact = async (contact) => {
    try {
      await blockContactApi(contact.id);
      setContacts(prev => prev.map(c =>
        c.id === contact.id ? { ...c, isBlacklisted: true } : c
      ));
    } catch (err) {
      console.error("[ContactsPage] blockContact:", err);
    }
  };

  const handleUnblockContact = async (contact) => {
    try {
      await unblockContactApi(contact.id);
      setContacts(prev => prev.map(c =>
        c.id === contact.id ? { ...c, isBlacklisted: false } : c
      ));
    } catch (err) {
      console.error("[ContactsPage] unblockContact:", err);
    }
  };

  // ── Blacklist handlers ────────────────────────────────────────────────────────
  const handleAddBlacklist = async ({ phone, scope, agentId }) => {
    try {
      const res = await addToBlacklist({ phone, scope, agentId });
      const entry = res?.data || res;
      setBlacklist(prev => [entry, ...prev]);
    } catch (err) {
      console.error("[ContactsPage] addBlacklist:", err);
    }
  };

  const handleRemoveBlacklist = async (entry) => {
    try {
      await removeFromBlacklist(entry.id);
      // Don't optimistically update — ContactManager.loadBlacklist() will fetch fresh data
    } catch (err) {
      console.error("[ContactsPage] removeBlacklist:", err);
    }
  };

  // ── User settings handlers ────────────────────────────────────────────────────
  const handleForwardSave = async (phone) => {
    try {
      await updateForwardPhone(phone);
      setForwardPhone(phone);
    } catch (err) {
      console.error("[ContactsPage] updateForwardPhone:", err);
    }
  };

  const handleRoutingToggle = async (enabled) => {
    try {
      await updateContactRouting(enabled);
      setRoutingEnabled(enabled);
    } catch (err) {
      console.error("[ContactsPage] updateContactRouting:", err);
    }
  };

  // ── Agent settings handlers ───────────────────────────────────────────────────
  const handleAgentSettingsSave = async (agentId, settings) => {
    try {
      await updateAgentSpamSettings(agentId, settings);
    } catch (err) {
      console.error("[ContactsPage] updateAgentSpamSettings:", err);
    }
  };

  const handleAgentSettingsLoad = async (agentId) => {
    try {
      const res = await getAgentSpamSettings(agentId);
      return res?.data || res || {};
    } catch (err) {
      console.error("[ContactsPage] getAgentSpamSettings:", err);
      return {};
    }
  };

  // ── Sync status banner ────────────────────────────────────────────────────────
  // ── Google reconnect ─────────────────────────────────────────────────────────
  const handleGoogleReconnect = async () => {
    try {
      setSyncStatus(null);
      const isRN = !!window.ReactNativeWebView;
      const status = await getGoogleSyncStatus({ isMobile: isRN, flow: "contacts" });
      if (status.authUrl) {
        if (isRN) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({ type: "OPEN_GOOGLE_AUTH", authUrl: status.authUrl, flow: "contacts" })
          );
        } else {
          window.location.href = status.authUrl;
        }
      }
    } catch (err) {
      console.error("[ContactsPage] reconnect error:", err);
    }
  };

  return (
    <div className={styles.page}>
      {/* Header — suppressed in onboarding context to avoid two
          competing chrome layers (the wrapper provides its own). */}
      {!hideHeader && (
        <div className={styles.header}>
          <HeaderBar
            title="Contacts"
            backgroundColor="#6524EB"
            color="#ffffff"
            onBack={() => {
              // Avoid navigating back to Google OAuth redirect pages
              if (window.history.length > 2) {
                const prev = document.referrer || "";
                if (prev.includes("accounts.google.com") || prev.includes("google_sync")) {
                  navigate("/fast-agent-detail", { replace: true });
                } else {
                  navigate(-1);
                }
              } else {
                navigate("/fast-agent-detail", { replace: true });
              }
            }}
          />
        </div>
      )}

      {/* Login wale business ka naam — dropdown nahi, business profile se switch hota hai. */}
      {isTeamMemberCP && selectedOwnerName && (
        <div
          style={{
            padding: "10px 16px",
            background: "#F5F3FF",
            borderBottom: "1px solid rgba(101,36,235,0.15)",
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: "#6524EB" }}>
            Contacts for:
          </span>
          <strong style={{ fontSize: 13, color: "#333" }}>{selectedOwnerName}</strong>
          <span style={{ width: "100%", fontSize: 11, color: "#888" }}>
            Applies to all agents of this company.
          </span>
        </div>
      )}

      {/* Sync status banners */}
      {syncStatus === "syncing" && (
        <div style={{ padding: "10px 16px", background: "#EEF2FF", color: "#4338CA", fontSize: 13, fontWeight: 500, textAlign: "center", borderBottom: "1px solid rgba(0,0,0,0.06)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <span style={{ width: 12, height: 12, border: "2px solid #4338CA", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
          Syncing contacts...
        </div>
      )}
      {syncStatus?.startsWith("done") && (
        <div style={{ padding: "10px 16px", background: "#F0FFF4", color: "#2D7A4F", fontSize: 13, fontWeight: 500, textAlign: "center", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
          {syncStatus.includes(":") ? `Contacts synced — ${syncStatus.split(":")[1]}` : "Contacts synced successfully"}
        </div>
      )}
      {syncStatus === "error" && (
        <div style={{ padding: "10px 16px", background: "#FFF0F0", color: "#D63B3B", fontSize: 13, fontWeight: 500, textAlign: "center", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
          Sync failed. Please try again.
        </div>
      )}
      {syncStatus === "auth_needed" && (
        <div style={{ padding: "10px 16px", background: "#FFF7ED", color: "#C05621", fontSize: 13, fontWeight: 500, display: "flex", alignItems: "center", justifyContent: "center", gap: 10, borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
          <span>Google account not connected.</span>
          <button onClick={handleGoogleReconnect} style={{ background: "#C05621", color: "#fff", border: "none", borderRadius: 6, padding: "4px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            Connect Google
          </button>
          <button onClick={() => setSyncStatus(null)} style={{ background: "none", border: "none", color: "#C05621", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 0 }}>✕</button>
        </div>
      )}

      {/* Body */}
      <div className={styles.body}>
        {loading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <p>Loading...</p>
          </div>
        ) : error ? (
          <div className={styles.errorState}>
            <p>{error}</p>
            {/* Wrapped: loadAll now takes an options object, and passing the
                click event straight through would read `silent` off it. */}
            <button onClick={() => loadAll()}>Retry</button>
          </div>
        ) : (
          <ContactManager
            agentId={agentIdParam}
            agents={agents}
            contacts={contacts}
            blacklist={blacklist}
            routingEnabled={routingEnabled}
            forwardPhone={forwardPhone}
            onCreateContact={handleCreateContact}
            onUpdateContact={handleUpdateContact}
            onDeleteContact={handleDeleteContact}
            onBulkDeleteContacts={async (ids) => {
              await bulkDeleteContactsApi(ids);
            }}
            onBlockContact={handleBlockContact}
            onUnblockContact={handleUnblockContact}
            onSync={handleSync}
            onAddBlacklist={handleAddBlacklist}
            onRemoveBlacklist={handleRemoveBlacklist}
            onForwardSave={handleForwardSave}
            onRoutingToggle={handleRoutingToggle}
            onAgentSettingsSave={handleAgentSettingsSave}
            onAgentSettingsLoad={handleAgentSettingsLoad}
            onTriggerGoogleSync={handleGoogleSyncTrigger}
            autoOpenSync={autoOpenSync}
            onSyncDialogClose={onSyncDialogClose}
            /* Sync sirf apne account me — team member ke business me kabhi nahi. */
            allowSync={!isTeamMemberCP}
            readOnly={contactsReadOnly}
            /* Sirf team member ke liye — unke paas agents ki list nahi banti, to
               agent contacts is id se seedha nikalte hain. Owner ka "All agents"
               wala default jaisa tha waisa hi rehta hai. */
            defaultAgentId={isTeamMemberCP ? agentIdParam : ""}
            oauthReturnTo={oauthReturnTo}
          />
        )}
      </div>
    </div>
  );
};

export default ContactsPage;
// 